import { format } from "date-fns";
import { de } from "date-fns/locale";

import { ActionBlockButton } from "~/components/buttons/ActionBlockButton";
import { EntryCard } from "~/modules/dashboard/components/TimeTrackingCalendar/EntryCard.tsx";
import { getTrackingColor } from "~/modules/dashboard/components/TimeTrackingCalendar/entryStyles.ts";
import type { TimeTrackingExtendedType } from "~/modules/timeTracking/api/timeTracking/timeTrackingTypes.ts";
import { TimeTrackingTypeId } from "~/modules/timeTracking/api/timeTrackingType/timeTrackingTypeTypes.ts";
import {
	getTimeTrackingBarTheme,
} from "~/modules/timeTracking/components/components/Day/components/TimeTrackingBar/getTimeTrackingBarTheme.ts";
import { formatNumberWithComma } from "~/utils/numberUtils.ts";

type Props = {
	timeTracking: TimeTrackingExtendedType;
	onClickEdit: () => void;
	onClickDelete: () => void;
	onClickClose: () => void;
};

/** The open tracking. Its buttons follow the `update` and `delete` blocks of the backend. */
export function TrackingCard({ timeTracking, onClickEdit, onClickDelete, onClickClose }: Props) {
	const { theme, title } = getTimeTrackingBarTheme(timeTracking);
	const isProject = timeTracking.timeTrackingTypeId === TimeTrackingTypeId.Project;
	const date = format(new Date(timeTracking.date), "EEEE, d. MMMM", { locale: de });
	const hours = formatNumberWithComma(timeTracking.minutes / 60, 1, false);

	return (
		<EntryCard color={getTrackingColor(theme)}
				   title={title ?? "Erfassung"}
				   subtitle={`${date} · ${hours} h`}
				   onClose={onClickClose}>
			{isProject && !timeTracking.isBillable && (
				<div>
					<span className="rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-red-700 ring-1 ring-red-200">
						Nicht abrechenbar
					</span>
				</div>
			)}
			{isProject && (
				<dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs">
					<dt className="text-gray-500">Phase</dt>
					<dd>{timeTracking.projectPhaseTitle}</dd>
					<dt className="text-gray-500">PO</dt>
					<dd>{timeTracking.orderTitle}</dd>
					<dt className="text-gray-500">Ort</dt>
					<dd>{timeTracking.locationName}</dd>
				</dl>
			)}
			<p className="whitespace-pre-line text-gray-700">{timeTracking.text || "–"}</p>
			<div className="flex gap-2 pt-1">
				<ActionBlockButton block={timeTracking.update}
								   theme="accent"
								   size="sm"
								   onClick={onClickEdit}>
					Bearbeiten
				</ActionBlockButton>
				<ActionBlockButton block={timeTracking.delete}
								   theme="danger"
								   size="sm"
								   onClick={onClickDelete}>
					Löschen
				</ActionBlockButton>
			</div>
		</EntryCard>
	);
}
